// React Components
import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';

// Ant Design Components
import { Table, Tooltip } from 'antd';

// Utilities & Helpers
import { DEFAULT_CUR_ROUND_OFF, QUANTITY } from '../../../../../../apis/constants';
import { v4 as uuidv4 } from 'uuid';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';

// UI Components
import H3Text from '../../../../../../uilib/h3Text';
import H3Image from '../../../../../../uilib/h3Image';
import H3Progress from '@Components/Common/H3Progress';

// Images
import imageDefault from '@Images/icons/imageDefault.png';

// Common Components
import BatchesTable from '@Components/Common/BatchesTable';
import ProductCategoryLabel from '../../../../../Common/ProductCategoryLabel';
import HideValue from '../../../../../Common/RestrictedAccess/HideValue';

const ViewGRNLines = ({
  selectedGRN, loading, user, MONEY, selectedRowKeys, setState, priceMasking, isApInvoiceEnabled, visibleColumns, cfGRNLine,
}) => {
  const adjustmentColumns = useMemo(() => [
    {
      title: '',
      responsive: ['xs'],
      render: (item) => (
        <div className="mobile-list__item">
          <div onClick={() => setState({ showPRZModal: true, selectedDocumentId: item?.product_sku_info?.product_sku_id })}>
            <a>
              {`#${item?.product_sku_info?.internal_sku_code}`}
            </a>
          </div>
          <H3Text text={`Product Name - ${item.remarks}`} className="mobile-list__item-info" />
          <H3Text text={`Received  Quantity: - ${QUANTITY(item?.received_qty, item?.uom_info?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()})`} className="mobile-list__item-info" />
        </div>
      ),
    },
    {
      title: '',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => (
        <div style={{ width: '50px', height: '50px' }}>
          <H3Image alt="product" style={{ width: '50px', height: '50px', opacity: item?.product_sku_info?.assets?.[0]?.url ? 1 : 0.15 }} src={item?.product_sku_info?.assets?.[0]?.url || imageDefault} />
        </div>
      ),
    },
    {
      title: 'SKU#',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
          {record?.product_sku_info?.internal_sku_code}
        </Link>
      ),
    },
    {
      title: 'Product Name',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => (
        <div style={{ Width: '100px' }}>
          {item?.product_sku_info?.product_sku_name}
          <H3Text text={item.remarks} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'Received  Quantity',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => `${QUANTITY(record?.received_qty, record?.uom_info?.precision)} ${record?.uom_info?.[0]?.uqc?.toProperCase()}`,
    },
  ], []);

  const getGRNColumns = () => {
    const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

    const isVendorOverseas = (selectedGRN?.seller_info?.seller_type === 'OVERSEAS' || selectedGRN?.seller_type === 'OVERSEAS') && user?.tenant_info?.purchase_config?.sub_modules?.vendor?.settings?.hide_tax_for_overseas;

    const grnColumns = [
      {
        title: '',
        responsive: ['xs'],
        render: (item) => (
          <div className="mobile-list__item">
            <Link to={`/inventory/product/view/${item?.product_sku_info?.product_sku_id}`} target="_blank">
              <H3Text text={`SKU# : ${item?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
            </Link>

            <H3Text text={`Product Name: ${item?.product_sku_info?.product_sku_name}`} className="mobile-list__item-info" />
            <H3Text text={`Ordered Qty: ${QUANTITY(item?.ordered_qty || '0', item?.uom_info[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()})`} className="mobile-list__item-info" />
            <H3Text text={`Received Qty: ${QUANTITY(item?.received_qty, item?.uom_info?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()})`} className="mobile-list__item-info" />
            <H3Text text={`Returned Qty: ${QUANTITY(item?.received_qty || '0', item?.uom_info[0]?.precision)} ${item?.uom_info[0]?.uqc?.toProperCase()}`} className="mobile-list__item-info" />
            {!isApInvoiceEnabled && (<H3Text text={`Unit Price: ${MONEY(item?.offer_price)}`} className="mobile-list__item-info" />)}
            {(selectedGRN?.seller_info?.seller_type !== 'OVERSEAS' && !isApInvoiceEnabled) && (<H3Text text={`Tax: ${item?.tax_info?.tax_value}%`} className="mobile-list__item-info" />)}
            {!isApInvoiceEnabled && (<H3Text text={`Total: ${MONEY(Number(item.received_qty) * Number(item.offer_price) * (1 + Number(item?.tax_info?.tax_value) / 100))}`} className="mobile-list__item-info" />)}
          </div>
        ),
      },
      {
        title: 'Product',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (
          <div style={{ maxWidth: '220px' }}>
            <Link
              onClick={() => setState({ showPRZModal: true, selectedDocumentId: item?.product_sku_info?.product_sku_id })}
            >
              {`#${item?.product_sku_info?.internal_sku_code}`}
            </Link>
            <div>
              {`${item?.product_sku_info?.ref_product_code ? `${item?.product_sku_info?.ref_product_code} - ` : ''}${item?.product_sku_info?.product_sku_name}`?.trim()}
              <div
                dangerouslySetInnerHTML={{ __html: item?.remarks }}
                className="table-subscript"
                style={{
                  width: '200px',
                  whiteSpace: 'pre-wrap',
                }}
              />
            </div>
            {item?.po_number &&
              <div>
                <Link
                  to={`/approval?type=po&id=${item?.po_id}`}
                  target="_blank"
                >
                  {`${item?.po_number || ''}`}
                </Link>
              </div>
            }
            {item?.product_sku_info?.product_category_info?.category_path?.length > 0 && (
              <ProductCategoryLabel
                categoryPath={item?.product_sku_info?.product_category_info?.category_path}
                categoryName={item?.product_sku_info?.product_category_info?.category_path?.at(
                  -1
                )}
                containerStyle={{
                  width: 'fit-content',
                }}
              />
            )}
          </div>
        ),
        visible: visibleColumns?.SKU?.visible,
      },
      {
        title: 'Received',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {selectedGRN?.grn_entity_type === 'GOOD_RECEIVING_NOTE' ? <Fragment>{`${record?.received_qty} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}`}</Fragment> : (
              <Tooltip title={(
                <div>
                  <H3Text
                    text={`Ordered - ${record?.ordered_qty || '0'} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}`}
                  />
                  <H3Text
                    text={`Received - ${record?.received_qty} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}`}
                  />
                </div>
              )}
              >
                {`${record?.received_qty} ${record?.uom_info?.[0]?.uqc?.toProperCase() || ''}`}
                <H3Progress percent={Number.parseInt((record?.received_qty / (record?.ordered_qty > 0 ? record?.ordered_qty : 1)) * 100)} barWidth="80px" />
              </Tooltip>
            )}
            {record?.secondary_uom_qty > 0 && user?.tenant_info?.global_config?.settings?.enable_secondary_uom && (
              <div>
                {`${QUANTITY(record?.secondary_uom_qty, (record?.product_sku_info?.secondary_uom_info?.precision || 0))} ${record?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || 'N/A'} (Secondary)`}
              </div>
            )}
            {Number(record?.dn_qty) > 0 && (
              <H3Text
                text={`(${QUANTITY(record?.dn_qty || '0', record?.uom_info[0]?.precision)} ${record?.uom_info[0]?.uqc?.toProperCase()} Returned)`}
                className="danger-subtext"
              />
            )}
            {(selectedGRN?.grn_entity_type == 'PURCHASE_ORDER' && record?.ordered_qty <= 0) ? <div className="status-tag" style={{ backgroundColor: '#468bf7' }}>ADHOC</div> : ''}
          </div>
        ),
        visible: visibleColumns?.RECEIVED?.visible,
      },
      {
        title: 'Invoice Quantity',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => `${QUANTITY(record?.invoice_quantity, record?.uom_info?.precision)} ${record?.uom_info?.[0]?.uqc?.toProperCase()}`,
        visible: isApInvoiceEnabled && visibleColumns?.INVOICE_QUANTITY?.visible,
      },
      {
        title: 'Unit Price',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => {

          return (
            <div>
              {(isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={'You don\'t have access to view unit price'} /> : MONEY((item?.offer_price), selectedGRN?.org_currency_info?.currency_code)}
              {!isVendorOverseas && (<H3Text
                text={`+ tax@${item?.tax_info?.tax_value}%`}
                className="danger-subtext"
              />)}
            </div>
          );
        },
        visible: !isApInvoiceEnabled && visibleColumns?.UNIT_PRICE?.visible,
      },
      {
        title: 'Discount',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <Fragment>
            {
              Number(record?.line_discount_amount) > 0
                ? (
                  <Fragment>
                    {(record?.is_discount_in_percent ? `${Number(record?.line_discount_percentage).toPrecision(DEFAULT_CUR_ROUND_OFF) || '0'}%` : MONEY(record?.line_discount_amount, selectedGRN?.org_currency_info?.currency_code))}
                  </Fragment>
                )
                : '-'
            }
          </Fragment>
        ),
        visible: !isApInvoiceEnabled && visibleColumns?.DISCOUNT?.visible,
        className: selectedGRN?.is_line_wise_discount ? '' : 'display-none',
      },
      {
        title: 'Total',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={'You don\'t have access to view unit price'} /> : MONEY((((Number(item.received_qty) * Number(item.offer_price)) * (item.line_discount_percentage ? Number(100 - item.line_discount_percentage) / 100 : 1)) * (1 + Number(item?.tax_info?.tax_value) / 100)), selectedGRN?.org_currency_info?.currency_code),
        visible: !isApInvoiceEnabled && visibleColumns?.TOTAL?.visible,
      },
    ];

    if (cfGRNLine?.length) {
      grnColumns.splice(5, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfGRNLine, visibleColumns));
    }

    return grnColumns?.filter((item) => item.visible) || [];
  };

  return (
    <Table
      bordered
      showHeader
      title={() => `Received Items (${selectedGRN?.grn_lines?.length})`}
      loading={loading}
      size="small"
      columns={selectedGRN?.grn_entity_type === 'INVENTORY_TRANSFER' ? adjustmentColumns : getGRNColumns()}
      dataSource={selectedGRN?.grn_lines?.map((item) => ({ ...item, key: uuidv4(), lineCustomFields: item?.grn_line_custom_fields })) || []}
      pagination={false}
      scroll={{ x: 'max-content' }}
      rowKey="grn_line_id"
      expandable={{
        rowExpandable: (record) => record?.product_batches?.length,
        expandedRowRender: (record) => (
          <BatchesTable
            title="Received Batches"
            batches={record?.product_batches}
            uomInfo={record?.uom_info?.[0]}
            expiryDays={record?.product_sku_info?.expiry_days}
            showCancel
            productInfo={record?.product_sku_info}
            hideCostPrice
          />
        ),
      }}
      rowSelection={(window.screen.width > 425 && user?.tenant_info?.global_config?.sub_modules?.barcoding?.is_active) ? {
        onChange: (rowKeys, rows) => {
          setState({ selectedRowKeys: rowKeys, selectedRows: rows });
        },
        selectedRowKeys,
      } : false}
    />
  );
};

export default ViewGRNLines;